/**
 * useIncassoKanbanData
 *
 * Per-column data fetching hook for the incasso kanban board.
 * Fetches tickets per phase with pagination: GET /rental/incasso-tickets?phase=X&page=N&pageSize=15
 *
 * Each column gets its own pagination state so users can load more within a column
 * without affecting other columns. Pagination resets when filters change.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import {
  IncassoTicket,
  IncassoPhase,
  IncassoTicketPage,
  IncassoTicketQueryParams,
} from '../incassoTypes';

const PAGE_SIZE = 15;

interface ColumnState {
  tickets: IncassoTicket[];
  page: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
}

type ColumnStates = Partial<Record<IncassoPhase, ColumnState>>;

interface UseIncassoKanbanDataOptions {
  phases: IncassoPhase[];
  filters: Omit<IncassoTicketQueryParams, 'phase' | 'page' | 'pageSize'>;
  /** When true, re-fetch all columns */
  enabled?: boolean;
}

const createDefaultState = (): ColumnState => ({
  tickets: [],
  page: 1,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
});

export const useIncassoKanbanData = ({
  phases,
  filters,
  enabled = true,
}: UseIncassoKanbanDataOptions) => {
  const { session } = useSession();

  // Track column states for each phase
  const [columnStates, setColumnStates] = useState<ColumnStates>(() => {
    const initial: ColumnStates = {};
    phases.forEach((phase) => {
      initial[phase] = createDefaultState();
    });
    return initial;
  });

  // Ref to track the current filters for dependency comparison
  const filtersRef = useRef(filters);

  /**
   * Fetch tickets for a single column/phase.
   * @param phase - The phase to fetch
   * @param page - The page to fetch (1 = reset)
   * @param isLoadMore - Whether we're loading more (appending) or fresh fetch
   */
  const fetchColumn = useCallback(
    async (phase: IncassoPhase, page: number, isLoadMore: boolean) => {
      if (!session) return;

      setColumnStates((prev) => ({
        ...prev,
        [phase]: {
          ...(prev[phase] ?? createDefaultState()),
          isLoading: !isLoadMore,
          isLoadingMore: isLoadMore,
        },
      }));

      try {
        const params = new URLSearchParams();
        params.set('phase', phase);
        params.set('page', String(page));
        params.set('pageSize', String(PAGE_SIZE));

        if (filters.search) params.set('search', filters.search);
        if (filters.beoordeling) params.set('beoordeling', filters.beoordeling);
        if (filters.buildingNames?.length) params.set('buildingNames', filters.buildingNames.join(','));
        if (filters.companyNames?.length) params.set('companyNames', filters.companyNames.join(','));
        if (filters.debtBucket) params.set('debtBucket', filters.debtBucket);
        if (filters.includeSnoozed !== undefined) {
          params.set('includeSnoozed', String(filters.includeSnoozed));
        }
        if (filters.noIncassoActions) params.set('noIncassoActions', 'true');
        // Default sort: highest outstanding amount first
        params.set('sortBy', filters.sortBy || 'cachedTotalDebt');
        params.set('sortDirection', filters.sortDirection || 'DESC');

        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets?${params}`,
          { method: 'GET' },
          session,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch incasso tickets for phase ${phase}`);
        }

        const data: IncassoTicketPage = await response.json();

        setColumnStates((prev) => {
          const existing = prev[phase] ?? createDefaultState();
          return {
            ...prev,
            [phase]: {
              tickets: isLoadMore ? [...existing.tickets, ...data.data] : data.data,
              page: data.page,
              hasMore: data.hasMore,
              isLoading: false,
              isLoadingMore: false,
            },
          };
        });
      } catch (error) {
        console.error(`[useIncassoKanbanData] Error fetching phase ${phase}:`, error);
        toast.error(`Fout bij ophalen van dossiers voor fase ${phase}`);

        setColumnStates((prev) => ({
          ...prev,
          [phase]: {
            ...(prev[phase] ?? createDefaultState()),
            isLoading: false,
            isLoadingMore: false,
          },
        }));

        throw error;
      }
    },
    [session, filters],
  );

  /**
   * Load more tickets for a specific column.
   */
  const loadMore = useCallback(
    (phase: IncassoPhase) => {
      const state = columnStates[phase];
      if (!state || !state.hasMore || state.isLoadingMore) return;
      fetchColumn(phase, state.page + 1, true);
    },
    [columnStates, fetchColumn],
  );

  /**
   * Refresh all columns (reset to page 1).
   */
  const refreshAll = useCallback(async () => {
    await Promise.all(phases.map((phase) => fetchColumn(phase, 1, false)));
  }, [phases, fetchColumn]);

  /**
   * Optimistically update a ticket across all columns.
   */
  const updateTicketLocally = useCallback(
    (ticketId: string, updates: Partial<IncassoTicket>) => {
      setColumnStates((prev) => {
        const next = { ...prev };
        phases.forEach((phase) => {
          const col = prev[phase];
          if (col) {
            next[phase] = {
              ...col,
              tickets: col.tickets.map((t) =>
                String(t.id) === String(ticketId) ? { ...t, ...updates } : t,
              ),
            };
          }
        });
        return next;
      });
    },
    [phases],
  );

  /**
   * Move a ticket from one phase column to another (optimistic).
   */
  const moveTicketLocally = useCallback(
    (ticketId: string, fromPhase: IncassoPhase, toPhase: IncassoPhase) => {
      setColumnStates((prev) => {
        const fromCol = prev[fromPhase];
        const toCol = prev[toPhase];
        if (!fromCol || !toCol) return prev;

        const ticket = fromCol.tickets.find((t) => String(t.id) === String(ticketId));
        if (!ticket) return prev;

        const updatedTicket = { ...ticket, phase: toPhase };

        return {
          ...prev,
          [fromPhase]: {
            ...fromCol,
            tickets: fromCol.tickets.filter((t) => String(t.id) !== String(ticketId)),
          },
          [toPhase]: {
            ...toCol,
            tickets: [updatedTicket, ...toCol.tickets],
          },
        };
      });
    },
    [],
  );

  // Fetch all columns on mount and when filters change
  useEffect(() => {
    if (!enabled || !session) return;

    const filtersChanged =
      JSON.stringify(filtersRef.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      filtersRef.current = filters;
    }

    // Reset all columns and refetch on page 1
    setColumnStates(() => {
      const reset: ColumnStates = {};
      phases.forEach((phase) => {
        reset[phase] = { ...createDefaultState(), isLoading: true };
      });
      return reset;
    });

    phases.forEach((phase) => {
      fetchColumn(phase, 1, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, session, JSON.stringify(filters)]);

  /**
   * Build the columnPagination array expected by HelpdeskKanbanView / Kanban component.
   */
  const columnPagination = phases.map((phase) => {
    const state = columnStates[phase] ?? createDefaultState();
    return {
      columnId: phase,
      hasMore: state.hasMore,
      isLoadingMore: state.isLoadingMore,
      onLoadMore: () => loadMore(phase),
      loadMoreText: 'Meer dossiers laden',
      loadingText: 'Laden...',
    };
  });

  /**
   * Get all tickets across all columns (flattened).
   */
  const allTickets: IncassoTicket[] = phases.flatMap(
    (phase) => columnStates[phase]?.tickets ?? [],
  );

  return {
    columnStates,
    columnPagination,
    allTickets,
    loadMore,
    refreshAll,
    updateTicketLocally,
    moveTicketLocally,
  };
};
